import type { Client } from '@src/clients/domain/Client';
import { FormTextInput } from '@src/common/components/FormTextInput';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { FormEntries } from '@src/orders/FormEntries';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { FormDatePicker } from '@src/orders/PermitFormSections/FormDatePicker';
import { FormNumberInput } from '@src/orders/PermitFormSections/FormNumberInput';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  InsuranceAddressNumberField,
  InsuranceAgentField,
  InsuranceCompanyField,
  InsuranceCoverageAmountField,
  InsuranceEffectiveDateField,
  InsuranceExperienceDateField,
  insuranceInformationFields,
  InsuranceIsOnFileWithStateField,
  InsurancePhoneNumberField,
  InsurancePolicyNumberField,
} from '@src/permitField/InsuranceInformationFields';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = { client: Client; fields: string[] } & CollapseListener;

export const InsuranceInformationSection = (props: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();
  const {
    register,
    control,
    formState: { errors, disabled: formDisabled },
  } = useFormContext<FormEntries<typeof props.fields>>();

  const { sectionInError } = useSectionError(
    Object.keys(insuranceInformationFields),
  );

  if (props.fields.length <= 0) {
    return null;
  }

  return (
    <EditOrderCollapsible
      title={t('insuranceInformation')}
      id="insurance-information"
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
      inError={sectionInError}
    >
      <Stack gap={theme.spacing['3']}>
        <ConditionalField
          fields={props.fields}
          conditions={InsuranceCompanyField.fieldName}
        >
          <FormTextInput
            register={register}
            errors={errors}
            disabled={
              formDisabled || isFieldDisabled(InsuranceCompanyField.fieldName)
            }
            fieldName={InsuranceCompanyField.fieldName}
          />
        </ConditionalField>
        <ConditionalField
          fields={props.fields}
          conditions={InsuranceAddressNumberField.fieldName}
        >
          <FormTextInput
            register={register}
            errors={errors}
            disabled={
              formDisabled ||
              isFieldDisabled(InsuranceAddressNumberField.fieldName)
            }
            fieldName={InsuranceAddressNumberField.fieldName}
          />
        </ConditionalField>

        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={InsuranceEffectiveDateField.fieldName}
          >
            <FormDatePicker
              isUTC
              control={control}
              disabled={
                formDisabled ||
                isFieldDisabled(InsuranceEffectiveDateField.fieldName)
              }
              fieldName={InsuranceEffectiveDateField.fieldName}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={InsuranceExperienceDateField.fieldName}
          >
            <FormDatePicker
              isUTC
              control={control}
              disabled={
                formDisabled ||
                isFieldDisabled(InsuranceExperienceDateField.fieldName)
              }
              fieldName={InsuranceExperienceDateField.fieldName}
            />
          </ConditionalField>
        </Stack>

        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={InsurancePolicyNumberField.fieldName}
          >
            <FormTextInput
              register={register}
              errors={errors}
              disabled={
                formDisabled ||
                isFieldDisabled(InsurancePolicyNumberField.fieldName)
              }
              fieldName={InsurancePolicyNumberField.fieldName}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={InsuranceCoverageAmountField.fieldName}
          >
            <FormNumberInput
              register={register}
              errors={errors}
              disabled={
                formDisabled ||
                isFieldDisabled(InsuranceCoverageAmountField.fieldName)
              }
              fieldName={InsuranceCoverageAmountField.fieldName}
            />
          </ConditionalField>
        </Stack>

        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={InsuranceAgentField.fieldName}
          >
            <FormTextInput
              register={register}
              errors={errors}
              disabled={
                formDisabled || isFieldDisabled(InsuranceAgentField.fieldName)
              }
              fieldName={InsuranceAgentField.fieldName}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={InsurancePhoneNumberField.fieldName}
          >
            <FormTextInput
              register={register}
              errors={errors}
              disabled={
                formDisabled ||
                isFieldDisabled(InsurancePhoneNumberField.fieldName)
              }
              fieldName={InsurancePhoneNumberField.fieldName}
            />
          </ConditionalField>
        </Stack>

        <ConditionalField
          fields={props.fields}
          conditions={InsuranceIsOnFileWithStateField.fieldName}
        >
          <ControllerFormCheckbox
            control={control}
            fieldName={InsuranceIsOnFileWithStateField.fieldName}
            disabled={
              formDisabled ||
              isFieldDisabled(InsuranceIsOnFileWithStateField.fieldName)
            }
          />
        </ConditionalField>
      </Stack>
    </EditOrderCollapsible>
  );
};
